<template>
  <!-- 관리자 예약 상세 '컨셉 변경'에서 할인 체험단 예약을 정가 컨셉으로 바꿀 때 여는 창: 받을 차액을 보이고 손님 결제 QR을 띄운다. -->
  <v-dialog :value="true" width="460" max-width="calc(100vw - 32px)" content-class="rounded-xl" persistent @keydown.esc="close">
    <v-card>
      <v-card-title>컨셉 변경 차액</v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle class="black--text text-body-1 font-weight-medium mt-2" style="word-break: keep-all">
        {{ $getUserDisplayName(reservation.name) }} 예약 · {{ q.fromConcept }} → {{ q.toConcept }}
      </v-card-subtitle>

      <v-card-text v-if="phase === 'quote'" class="px-6">
        <dl class="diff-table">
          <div>
            <dt>정가<template v-if="q.persons > 1">({{ q.persons }}명)</template></dt>
            <dd>{{ won(q.regularMain) }}</dd>
          </div>
          <div>
            <dt>낸 금액(할인 체험단)</dt>
            <dd>− {{ won(q.chargedMain) }}</dd>
          </div>
          <div v-if="q.paid">
            <dt>이미 받은 차액</dt>
            <dd>− {{ won(q.paid) }}</dd>
          </div>
          <div class="diff-total">
            <dt>받을 차액</dt>
            <dd>{{ won(q.amount) }}</dd>
          </div>
        </dl>
        <p v-if="!noDifference" class="text-body-2 grey--text text--darken-1 mb-0 mt-4" style="word-break: keep-all">
          QR을 띄워 손님 휴대폰으로 결제받습니다. 결제가 끝나면 예약 컨셉이 바로 바뀝니다.
        </p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'qr'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-center">
        <div class="qr-frame" role="img" :aria-label="`손님 결제 QR, ${won(order.amount)}`">
          <vue-qrcode :value="payUrl" :options="{ width: 232, margin: 1 }" />
        </div>
        <div class="qr-amount">{{ won(order.amount) }}</div>
        <p class="d-flex align-center justify-center text-body-2 font-weight-bold mb-1" aria-hidden="true">
          <v-progress-circular indeterminate size="16" width="2" color="#7d1832" class="qr-wait-spinner mr-2" />
          {{ TEXT.waiting }}
        </p>
        <p class="qr-note text-body-2 grey--text text--darken-1 mb-0">
          <span>손님 휴대폰 카메라로 찍게 해 주세요. 결제가 끝나면 이 창이 저절로 바뀝니다.</span>
          <span>창을 닫아도 손님이 결제를 마치면 예약 컨셉은 바뀝니다.</span>
        </p>
      </v-card-text>

      <v-card-text v-else-if="phase === 'paid'" ref="phaseBody" tabindex="-1" class="phase-body px-6 text-body-1 font-weight-bold black--text" style="word-break: keep-all">{{ paidText }}</v-card-text>

      <!-- 화면 읽기 프로그램이 단계가 바뀐 것을 읽도록, 알림 자리는 늘 두고 글만 바꾼다 -->
      <p class="d-sr-only" role="status">{{ statusText }}</p>
      <div class="px-6" :class="{ 'text-center': phase === 'qr' }" role="alert">
        <p v-if="alertText" class="pt-3 mb-0 text-body-2 font-weight-bold" style="color: #a53a66; word-break: keep-all">{{ alertText }}</p>
      </div>

      <v-card-actions class="pb-4">
        <v-spacer />
        <v-btn ref="closeBtn" class="px-4" depressed outlined :disabled="busy" :loading="busy && phase === 'qr'" @click="close">닫기</v-btn>
        <v-btn v-if="phase === 'quote' && !noDifference" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" @click="createOrder">QR 띄우기</v-btn>
        <v-btn v-if="phase === 'paidNotApplied'" ref="applyBtn" class="px-4 mx-4 white--text" color="#7d1832" depressed :loading="busy" @click="applyConcept">컨셉 바꾸기</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VueQrcode from "@chenfengyuan/vue-qrcode"

// 통신이 끊겨도 단추가 도는 채로 갇히지 않게, 이 창의 요청은 이 시간 안에 끝낸다(시간 변경 창과 같은 값).
const REQUEST_TIMEOUT_MS = 15000
// 손님이 결제했는지 이 간격으로 확인한다. 이 횟수만큼 잇달아 끊기면 직원에게 알린다.
const POLL_MS = 3000
const POLL_FAILURE_ALERT = 3
// 이 화면에서 이미 띄운 차액 주문(예약·컨셉·금액이 같으면 새로 만들지 않고 같은 QR을 다시 띄운다).
// 창을 닫았다 다시 열 때 결제할 수 있는 주문이 둘 생겨, 손님이 앞 QR로 결제했는데 직원 창은 뒤 QR을 기다리는 일을 막는다.
const OPEN_ORDERS = new Map()
const TEXT = {
  waiting: "손님 결제를 기다리고 있습니다.",
  notApplied: "결제는 받았지만 예약 컨셉이 바뀌지 않았습니다. '컨셉 바꾸기'를 눌러 주세요.",
  offline: "연결이 끊겨 결제를 확인하지 못하고 있습니다. 인터넷 연결을 확인하고, 손님 휴대폰에 결제 완료 화면이 떴는지 봐 주세요.",
  orderFailed: "결제 QR을 만들지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
  noDifference: "받을 차액이 없어졌습니다. 창을 닫고 컨셉을 다시 골라 주세요.",
  applyFailed: "컨셉을 바꾸지 못했습니다. 잠시 뒤 다시 눌러 주세요.",
}

export default {
  name: "ConceptChangeDiffDialog",

  components: { VueQrcode },

  props: {
    // 관리자 예약 목록의 한 줄(id가 예약 번호)
    reservation: { type: Object, required: true },
    // POST /api/addon-pay/concept-change/quote 응답
    quote: { type: Object, required: true },
  },

  data: () => ({
    TEXT,
    phase: "quote", // quote | qr | paid | paidNotApplied
    order: null,
    busy: false,
    errorMessage: "",
    pollFailures: 0,
    timer: null,
    // 창을 닫은 뒤에는 결제 확인을 다시 걸지 않는다
    closed: false,
    // 주문을 만들려는데 받을 차액이 없어졌으면(그새 받음 등) 서버가 다시 계산한 값과 그 표시
    serverQuote: null,
    noDifference: false,
    // 단추가 잠긴 동안 바뀐 단계는 단추가 풀린 뒤 초점을 옮긴다
    pendingFocus: false,
  }),

  computed: {
    q() {
      return this.serverQuote || this.quote
    },
    // 테스트 진입점(…_test)에서 띄운 QR은 손님 결제 화면 테스트본으로 보낸다.
    payUrl() {
      const page = location.pathname.includes("_test") ? "addon_test.html" : "addon.html"
      return `${location.origin}/pay/${page}?change=${this.order.orderId}`
    },
    paidText() {
      return `${this.won(this.order && this.order.amount)} 결제를 받았고 예약 컨셉을 바꿨습니다.`
    },
    // 결제 받음 글은 초점이 가서 읽히므로 숨은 알림 자리에는 두지 않는다(두 번 읽히지 않게)
    statusText() {
      return this.phase === "qr" ? TEXT.waiting : ""
    },
    alertText() {
      if (this.errorMessage) return this.errorMessage
      if (this.phase === "paidNotApplied") return TEXT.notApplied
      if (this.phase === "qr" && this.pollFailures >= POLL_FAILURE_ALERT) return TEXT.offline
      return ""
    },
  },

  watch: {
    // 누른 단추가 사라지는 단계 전환 뒤에도 초점이 그 단계의 할 일에 가게 한다
    phase() {
      this.focusPhase()
    },
    busy(now) {
      if (!now && this.pendingFocus) this.focusPhase()
    },
  },

  beforeDestroy() {
    this.closed = true
    clearTimeout(this.timer)
  },

  methods: {
    won(value) {
      return `${Number(value || 0).toLocaleString("ko-KR")}원`
    },

    // 결제됐는데 안 바뀜 → '컨셉 바꾸기', QR 기다림·결제 받음 → 그 단계 본문
    focusPhase() {
      if (this.busy) {
        this.pendingFocus = true
        return
      }
      this.pendingFocus = false
      this.$nextTick(() => {
        // 받을 차액이 없어져 'QR 띄우기'가 사라지면 '닫기'로
        const target = this.phase === "paidNotApplied" ? this.$refs.applyBtn : this.noDifference && this.phase === "quote" ? this.$refs.closeBtn : this.$refs.phaseBody
        const el = target && (target.$el || target)
        if (el && el.focus) el.focus()
      })
    },

    orderKey() {
      return `${this.q.reservationId}:${this.q.toConceptId}:${this.q.amount}`
    },

    async createOrder() {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        let order = OPEN_ORDERS.get(this.orderKey())
        if (!order) {
          const { data } = await this.$http.post(
            "/api/addon-pay/concept-change/order",
            { reservationId: this.q.reservationId, conceptId: this.q.toConceptId },
            { timeout: REQUEST_TIMEOUT_MS }
          )
          order = data
          OPEN_ORDERS.set(this.orderKey(), order)
        }
        this.order = order
        this.phase = "qr"
        this.poll()
      } catch (error) {
        const body = (error && error.response && error.response.data) || {}
        if (body.code === "NO_DIFFERENCE") {
          this.serverQuote = body.quote || null
          this.noDifference = true
          this.focusPhase()
        }
        this.errorMessage = body.code === "NO_DIFFERENCE" ? TEXT.noDifference : TEXT.orderFailed
      } finally {
        this.busy = false
      }
    },

    // 주문을 다시 읽어 결제됐으면 끝낸다. 끊기면 false.
    async checkPaid(timeout = REQUEST_TIMEOUT_MS) {
      try {
        const { data } = await this.$http.get(`/api/addon-pay/concept-change/${this.order.orderId}`, { timeout })
        this.pollFailures = 0
        if (data.paid && !this.closed) this.finish(data.applied)
        return true
      } catch (error) {
        this.pollFailures += 1
        return false
      }
    },

    poll() {
      clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        await this.checkPaid()
        if (this.phase === "qr" && !this.closed) this.poll()
      }, POLL_MS)
    },

    finish(applied) {
      clearTimeout(this.timer)
      OPEN_ORDERS.delete(this.orderKey())
      if (!applied) {
        this.phase = "paidNotApplied"
        return
      }
      this.phase = "paid"
      this.$emit("changed", this.q.toConcept)
    },

    // 결제는 받았는데 예약 컨셉이 안 바뀌었을 때: 예전 컨셉 변경 경로로 바꾼다.
    async applyConcept() {
      if (this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        const { data } = await this.$http.post(
          "/api/reservation/update/change_concept",
          { concept: this.q.toConcept, id: this.reservation.id },
          { timeout: REQUEST_TIMEOUT_MS }
        )
        if (!data.affectedRows) throw new Error("not changed")
        this.finish(true)
      } catch (error) {
        this.errorMessage = TEXT.applyFailed
      } finally {
        this.busy = false
      }
    },

    // QR을 띄운 채 닫으면 마지막으로 한 번 더 확인한다. 그새 결제됐으면 닫지 않고 결제 받음을 보인다.
    async close() {
      if (this.busy) return
      if (this.phase === "qr") {
        clearTimeout(this.timer)
        this.busy = true
        // 연결이 끊긴 상태면 오래 기다리지 않는다
        await this.checkPaid(this.pollFailures >= POLL_FAILURE_ALERT ? 4000 : REQUEST_TIMEOUT_MS)
        this.busy = false
        if (this.phase !== "qr") return
      }
      this.closed = true
      clearTimeout(this.timer)
      this.$emit("close")
    },
  },
}
</script>

<style scoped>
.diff-table {
  margin: 0;
  font-variant-numeric: tabular-nums;
}
.diff-table > div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  color: rgba(0, 0, 0, 0.87);
}
.diff-table dd {
  margin: 0;
  white-space: nowrap;
}
.diff-total {
  margin-top: 6px;
  padding-top: 10px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 18px;
  font-weight: 800;
  color: #7d1832 !important;
}
.qr-frame {
  display: inline-block;
  padding: 10px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 3px -1px rgba(0, 0, 0, 0.1);
  line-height: 0;
}
.phase-body:focus {
  outline: none;
}
/* 코드로 옮긴 초점은 마우스·터치로 쓰는 직원에게 단추가 바랜 것처럼 보이게 하지 않는다(키보드 초점 표시는 그대로) */
.v-btn:focus:not(:focus-visible)::before {
  opacity: 0 !important;
}
.qr-note span {
  display: block;
  word-break: keep-all;
  text-wrap: balance;
}
/* 몇 분씩 도는 기다림 표시는 움직임 줄이기 설정이면 멈춘다(옆 글이 상태를 말한다) */
@media (prefers-reduced-motion: reduce) {
  .qr-wait-spinner {
    display: none !important;
  }
}
.qr-amount {
  margin: 14px 0 10px;
  font-size: 24px;
  font-weight: 800;
  color: #7d1832;
  font-variant-numeric: tabular-nums;
}
</style>
